import React from 'react';

/**
 * ConfirmDialog - A professional confirmation dialog component
 * Replaces browser's native window.confirm() with a styled modal
 */
const ConfirmDialog = ({ 
  isOpen, 
  title, 
  message, 
  type = 'warning',
  confirmText = 'Confirm', 
  cancelText = 'Cancel',
  onConfirm, 
  onCancel 
}) => {
  if (!isOpen) return null;

  // Define colors based on type
  const types = {
    danger: {
      iconColor: '#dc2626',
      iconBg: '#fee2e2',
      confirmBg: '#dc2626',
      confirmHover: '#b91c1c',
    },
    warning: {
      iconColor: '#d97706',
      iconBg: '#fef3c7',
      confirmBg: '#d97706',
      confirmHover: '#b45309',
    },
    info: {
      iconColor: '#2563eb',
      iconBg: '#dbeafe',
      confirmBg: '#2563eb',
      confirmHover: '#1d4ed8',
    },
    success: {
      iconColor: '#16a34a',
      iconBg: '#dcfce7',
      confirmBg: '#16a34a',
      confirmHover: '#15803d',
    }
  };

  const style = types[type] || types.warning;

  const iconSvg = type === 'danger' 
    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    : type === 'warning'
    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    : type === 'success'
    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      overflowY: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    }}>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          transition: 'opacity 150ms',
        }}
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div style={{
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        maxWidth: '400px',
        width: '100%',
        transform: 'transition-all',
      }}>
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {/* Icon */}
            <div style={{
              flexShrink: 0,
              backgroundColor: style.iconBg,
              borderRadius: '50%',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg 
                style={{ width: '18px', height: '18px', color: style.iconColor }} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {iconSvg}
              </svg>
            </div>
            
            {/* Text */}
            <div style={{ marginLeft: '16px', textAlign: 'left', flex: 1 }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
                margin: 0,
              }}>
                {title}
              </h3>
              <p style={{
                marginTop: '8px',
                fontSize: '14px',
                color: '#6b7280',
                margin: 0,
              }}>
                {message}
              </p>
            </div>
          </div>
        </div>
        
        {/* Buttons */}
        <div style={{
          backgroundColor: '#f9fafb',
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          borderRadius: '0 0 8px 8px',
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              display: 'inline-flex',
              justifyContent: 'center',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              cursor: 'pointer',
              transition: 'background-color 150ms',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              display: 'inline-flex',
              justifyContent: 'center',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: style.confirmBg,
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
              cursor: 'pointer',
              transition: 'background-color 150ms',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = style.confirmHover}
            onMouseOut={(e) => e.target.style.backgroundColor = style.confirmBg}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;